import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import head from 'lodash/head';
import map from 'lodash/map';
import remove from 'lodash/remove';
import sortBy from 'lodash/sortBy';

import { showLoading, hideLoading } from '../app';
import WebAPIClient, { errorResponseToastr } from '../../api';
import { RANGES } from '../../components/charts/selectors/SelectRange';
import { setLoader } from '../pages';
import { buildAsyncReducers } from '../thunkTemplate';
import { summaryKiosk } from '../initialState';
import { sumBy } from 'lodash';

function flattenAndSum(rawData) {
  const map = new Map();

  rawData.forEach(({ records }) => {
    (records ?? []).forEach(({ timestamp, value = 0 }) => {
      map.set(timestamp, (map.get(timestamp) ?? 0) + value);
    });
  });

  // Convert back to array, sorted by timestamp
  return Array.from(map.entries())
    .map(([timestamp, value]) => ({ timestamp, value }))
    .sort((a, b) => a.timestamp - b.timestamp);
}

const getKioskData = createAsyncThunk(
  'summaryKiosk/getKioskData',
  async (_, { dispatch, getState, requestId }) => {
    const { kioskCode, type, currentRequestId, loading } =
      getState().summaryKiosk;

    if (loading !== true || requestId !== currentRequestId) {
      return;
    }

    try {
      let resource = null;
      let sites = null;

      const payload = await new WebAPIClient().GET(
        `/kiosk/${type}/${kioskCode}`
      );
      let images = await new WebAPIClient().GET(
        `/kiosk/images/${type}/${kioskCode}`
      );

      const logo = head(
        remove(images, (image) => {
          return image.filename.startsWith('logo.');
        })
      );

      if (type === 'organization') {
        resource = get(payload, 'organization', {});
        sites = sortBy(get(payload, 'sites', []), 'name');
      } else if (type === 'site') {
        const site = get(payload, 'site', {});
        resource = site;
        sites = [site];
      }

      const meters = get(payload, 'meters', []);

      const powerByMonth = meters.reduce((acc, meter) => {
        (meter.energy_stats?.production ?? []).forEach((monthData) => {
          const monthKey = `${monthData.year}-${monthData.month}`;
          const value = monthData.value || 0;
          acc[monthKey] = (acc[monthKey] ?? 0) + value;
        });
        return acc;
      }, {});
      const powerByMonthArray = map(powerByMonth, (value, month) => ({
        month,
        value,
      }));
      const sortedPowerByMonth = sortBy(powerByMonthArray, (d) =>
        dayjs(d.month)
      );

      const lifetimePower = meters.reduce((acc1, meter) => {
        const meterLifetimePower = (
          meter.energy_stats?.production ?? []
        ).reduce((acc2, monthData) => {
          return acc2 + get(monthData, 'value', 0);
        }, 0);
        return acc1 + meterLifetimePower;
      }, 0);

      const lifetime = {
        total: lifetimePower,
        data: sortedPowerByMonth,
      };

      const thisYear = {
        total: sumBy(lifetime.data.slice(-12), 'value'),
        data: lifetime.data.slice(-12),
      };

      return {
        lastUpdate: dayjs(),
        validLicense: true,
        resource,
        sites,
        loggers: get(payload, 'loggers', []),
        meters,
        inverters: get(payload, 'inverters', []),
        images: {
          logo,
        },
        lifetime,
        thisYear,
      };
    } catch (err) {
      errorResponseToastr(err);
      return { validLicense: false };
    } finally {
      dispatch(setLoader(false));
    }
  }
);

const getThisMonthData = createAsyncThunk(
  'summaryKiosk/getThisMonthData',
  async (_, { dispatch, getState }) => {
    const { kioskCode, type, meters } = getState().summaryKiosk;

    try {
      dispatch(showLoading());
      const month = dayjs().startOf('month');

      const rawData = await Promise.all(
        map(meters, (meter) =>
          new WebAPIClient().GET(
            `/kiosk/${type}/${kioskCode}/${meter.meter_id}/${month.format(
              'YYYY-MM'
            )}`
          )
        )
      );
      const flattened = flattenAndSum(rawData);
      const total = flattened.reduce((sum, { value = 0 }) => sum + value, 0);
      return { thisMonth: { rawData, data: flattened, total } };
    } catch (err) {
      errorResponseToastr(err);
    } finally {
      dispatch(hideLoading());
    }
  }
);

const getLastMonthData = createAsyncThunk(
  'summaryKiosk/getLastMonthData',
  async (meters, { dispatch, getState, requestId }) => {
    const { kioskCode, type, lastMonth, currentRequestId, loading } =
      getState().summaryKiosk;
    if (loading !== true || requestId !== currentRequestId) {
      return;
    }
    try {
      dispatch(showLoading());
      const month = dayjs().subtract(1, 'month').startOf('month');

      const rawData = await Promise.all(
        map(meters, (meter) =>
          new WebAPIClient().GET(
            `/kiosk/${type}/${kioskCode}/${meter.meter_id}/${month.format(
              'YYYY-MM'
            )}`
          )
        )
      );

      const flattened = flattenAndSum(rawData);
      const total = flattened.reduce((sum, { value = 0 }) => sum + value, 0);

      return { lastMonth: { ...lastMonth, rawData, data: flattened, total } };
    } catch (err) {
      errorResponseToastr(err);
    } finally {
      dispatch(hideLoading());
    }
  }
);

const getTodayData = createAsyncThunk(
  'summaryKiosk/getTodayData',
  async (_, { dispatch, getState, requestId }) => {
    const {
      kioskCode,
      type,
      meters,
      // today,
      resource,
    } = getState().summaryKiosk;

    let defaultRange = RANGES[0];
    const start = dayjs().tz(resource.timezone.zone).startOf('day');
    const end = dayjs().tz(resource.timezone.zone).endOf('day');
    defaultRange = { ...defaultRange, start, end };

    let _range = defaultRange;
    try {
      dispatch(showLoading());
      let _type = type;
      if (_type === 'org') _type = 'organization';

      let rawData = await Promise.all(
        map(meters, (meter) =>
          new WebAPIClient().GET(
            `/kiosk/${_type}/${kioskCode}/${
              meter.meter_id
            }/${_range.start.unix()}/${_range.end.unix()}`
          )
        )
      );

      const flattened = flattenAndSum(rawData).map((entry) => ({
        ...entry,
        value: entry.value > 0 ? entry.value / 4 : 0,
      }));
      const total = flattened.reduce((sum, { value = 0 }) => sum + value, 0);

      return { today: { rawData, data: flattened, total } };
    } catch (err) {
      errorResponseToastr(err);
    } finally {
      dispatch(hideLoading());
    }
  }
);

const { actions, reducer } = createSlice({
  name: 'summaryKiosk',
  initialState: summaryKiosk,
  reducers: {
    setKioskTypeCode: (state, { payload }) => {
      return {
        ...state,
        kioskCode: payload.kioskCode,
        type: payload.type,
      };
    },
    handleSuccessKiosk: (state, { payload }) => {
      const meters = cloneDeep(state.meters);
      const timezone = state.resource?.timezone ?? { zone: 'UTC' };

      const end = dayjs().tz(timezone.zone).endOf('day');

      const timestamp = payload.timestamp;
      const orgId = payload.org_id;
      const loggerId = payload.logger_id;
      const loggerMeters = filter(meters, {
        logger_id: loggerId,
        org_id: orgId,
      });

      let rawData = cloneDeep(state.today.rawData);

      forEach(loggerMeters, (meter) => {
        const value = payload[`kW_Demand_System_Now_M${meter.parent_index}`];
        const recordDay = dayjs.unix(timestamp).tz(timezone.zone);

        if (typeof value === 'number' && recordDay.isSame(end, 'day')) {
          // Pull this meter’s series out
          let meterRawData = head(
            remove(rawData, { meter_id: meter.meter_id })
          ) ?? {
            meter_id: meter.meter_id,
            records: [],
          };

          // Dedupe: skip if we already have this timestamp
          const exists = meterRawData.records.some(
            (r) => r.timestamp === timestamp
          );
          if (!exists) {
            meterRawData.records.push({ timestamp, value });
          }

          // Keep today only
          meterRawData.records = meterRawData.records.filter((r) =>
            dayjs.unix(r.timestamp).tz(timezone.zone).isSame(end, 'day')
          );

          rawData.push(meterRawData);
        }
      });

      const flattened = flattenAndSum(rawData).map((e) => ({
        ...e,
        value: e.value / 4,
      }));
      const total = flattened.reduce((s, { value = 0 }) => s + value, 0);

      return { ...state, today: { rawData, data: flattened, total } };
    },
  },
  extraReducers: (builder) => {
    buildAsyncReducers(builder, [
      getKioskData,
      getThisMonthData,
      getLastMonthData,
      getTodayData,
    ]);
  },
});

const { setKioskTypeCode, handleSuccessKiosk } = actions;

export {
  setKioskTypeCode,
  getKioskData,
  getThisMonthData,
  getLastMonthData,
  getTodayData,
  handleSuccessKiosk,
};
export default reducer;
